import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Switch from '@core/components/ApplyDialogV2/ApplyV2Switch'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import { useFormik } from 'formik'
import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import './ApplyV2CoursesStepAdd.scss'

interface Props {
    className?: string
}

const baseClass = 'ApplyV2CoursesStepAdd'

function ApplyV2CoursesStepAdd({ className }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const validationSchema = Yup.object().shape({
        courseName: Yup.string()
            .trim()
            .required(`${t('5653', 'Required')}`)
            .min(2, `${t('9763', 'Minimum')} 2`)
            .max(255, `${t('5339', 'Max')} 255`),
        courseDescription: Yup.string()
            .trim()
            .required(t('5653', 'Required') ?? '')
            .min(2, `${t('9763', 'Minimum')} 2`)
            .max(5000, `${t('5339', 'Max')} 5000`),
        issueDate: Yup.boolean(),
        expireDate: Yup.boolean(),
        noExpireDate: Yup.boolean(),
        startDateDay: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
        startDateMonth: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
        startDateYear: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
        endDateDay: Yup.string().when(['expireDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
        endDateMonth: Yup.string().when(['expireDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
        endDateYear: Yup.string().when(['expireDate'], {
            is: true,
            then: Yup.string().required(t('5653', 'Required') ?? ''),
        }),
    })
    const formik = useFormik({
        initialValues: {
            courseName: '',
            courseDescription: '',
            issueDate: false,
            expireDate: false,
            noExpireDate: false,
            startDateDay: '',
            startDateMonth: '',
            startDateYear: '',
            endDateDay: '',
            endDateMonth: '',
            endDateYear: '',
        },
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                setSubmitting(true)
                // Add your submission logic here
                console.log('Form submitted with values:', values)
                await new Promise((resolve) => setTimeout(resolve, 1000))
                // On success:
                // resetForm()
            } catch (error) {
                console.error('Submission error:', error)
            } finally {
                setSubmitting(false)
                console.log('Form submission completed')
            }
        },

        validationSchema,
        // validateOnMount: false,
        // validateOnBlur: true,
        // validateOnChange: true,
        // enableReinitialize: true,
    })

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('8732', 'Add course or certificate')}</div>
                <ApplyV2Button
                    className={`${baseClass}__header__btn`}
                    size={'small'}
                    variant="transparent"
                    leftIcon={
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                fill="#19191A"
                            />
                        </svg>
                    }
                />
            </div>
            <ApplyV2InputText
                name="courseName"
                label={'DDD'}
                value={formik.values.courseName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseName && formik.errors.courseName}
                // errorMessage={formik.errors.courseName}
                isRequired
            />
            <ApplyV2InputTextarea
                name="courseDescription"
                label={'DDD'}
                value={formik.values.courseDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}
                isRequired
            />
            <ApplyV2Switch
                name="issueDate"
                checked={formik.values.issueDate}
                // onChange={formik.handleChange}
                onChange={(e) => {
                    formik.setFieldValue('issueDate', e.target.checked)
                    if (!e.target.checked) {
                        formik.setFieldValue('startDateDay', '')
                        formik.setFieldValue('startDateMonth', '')
                        formik.setFieldValue('startDateYear', '')
                    }
                }}
                onBlur={formik.handleBlur}
                label={getTitle('8733', 'Issue date')}
            />
            {formik.values.issueDate && (
                <ApplyV2DateSelectGroup
                    label={getTitle('7577', 'Expiry date')}
                    isRequired
                    names={{
                        day: 'startDateDay',
                        month: 'startDateMonth',
                        year: 'startDateYear',
                    }}
                    value={{
                        day: formik.values.startDateDay,
                        month: formik.values.startDateMonth,
                        year: formik.values.startDateYear,
                    }}
                    onChange={({ day, month, year }) => {
                        formik.setFieldValue('startDateDay', day)
                        formik.setFieldValue('startDateMonth', month)
                        formik.setFieldValue('startDateYear', year)
                    }}
                    errors={{
                        day: formik.touched.startDateDay && formik.errors.startDateDay,
                        month: formik.touched.startDateMonth && formik.errors.startDateMonth,
                        year: formik.touched.startDateYear && formik.errors.startDateYear,
                    }}
                    onBlur={formik.handleBlur}
                    isDisabled={!formik.values.issueDate}
                />
            )}
            <ApplyV2Button
                type={'submit'}
                variant="primary"
                className={`${baseClass}__submit`}
                isLoading={formik.isSubmitting}
                onClick={() => {
                    console.log('Submit clicked', formik.values)
                    console.log('Formik state:', formik.touched)
                    console.log('Formik errors:', formik.errors)
                }}
            >
                TEst
            </ApplyV2Button>
        </form>
    )
}

export default ApplyV2CoursesStepAdd
